import { useEffect, useRef, useState } from "react";

import { assetUrlToFile, getMediaTileFileIcon } from "@icg360/design-system";

import { type KeystoneInspectionAsset } from "data/keystone-api-mapping";

import { type MediaTileItem } from "./inspection-gallery-types";
import {
  blobSourceForInspectionFile,
  buildInspectionAssetDescription,
  galleryPlaceholderImageFile,
  inspectionAssetUsesDirectImageUrl,
  isDirectRasterImageMime,
  resolveGalleryImageMimeForUrl,
  type InspectionAssetDescriptionMode,
} from "./inspections-helpers";

type UseResolvedInspectionGalleryMediaArgs = {
  showGallery: boolean;
  shownAssetIds: string[];
  inspectionAssets: KeystoneInspectionAsset[] | undefined;
  agencyName: string | null | undefined;
  descriptionMode: InspectionAssetDescriptionMode;
};

export function useResolvedInspectionGalleryMedia({
  showGallery,
  shownAssetIds,
  inspectionAssets,
  agencyName,
  descriptionMode,
}: UseResolvedInspectionGalleryMediaArgs): {
  resolvedGalleryMedia: MediaTileItem[] | null;
  galleryMediaLoading: boolean;
} {
  const galleryBlobUrlsRef = useRef<string[]>([]);
  const [resolvedGalleryMedia, setResolvedGalleryMedia] = useState<
    MediaTileItem[] | null
  >(null);
  const [galleryMediaLoading, setGalleryMediaLoading] = useState(false);

  useEffect(() => {
    if (!showGallery) {
      galleryBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      galleryBlobUrlsRef.current = [];
      setResolvedGalleryMedia(null);
      setGalleryMediaLoading(false);
      return;
    }

    let cancelled = false;

    setGalleryMediaLoading(true);
    setResolvedGalleryMedia(null);

    (async () => {
      const newBlobUrls: string[] = [];
      const items: MediaTileItem[] = await Promise.all(
        shownAssetIds.map(async (assetId) => {
          const selectedAsset = inspectionAssets?.find(
            (asset) => asset.id === assetId,
          );
          const resolvedName =
            selectedAsset?.assetFileName ?? `asset_${assetId}`;
          const fileMetadata = buildInspectionAssetDescription(
            selectedAsset,
            agencyName,
            descriptionMode,
          );
          const assetUrl = `/api/inspection/image/${assetId}`;
          const mimeFromApi =
            selectedAsset?.assetType ?? "application/octet-stream";
          const useDirectImageUrl =
            inspectionAssetUsesDirectImageUrl(selectedAsset);

          let file: File | undefined;
          let source = "";

          if (useDirectImageUrl) {
            source = assetUrl;
            file = galleryPlaceholderImageFile(
              resolvedName,
              resolveGalleryImageMimeForUrl(mimeFromApi),
            );
          } else {
            try {
              file = await assetUrlToFile(assetUrl, resolvedName, mimeFromApi);
              source = blobSourceForInspectionFile(file);
              if (source.startsWith("blob:")) {
                newBlobUrls.push(source);
              }
            } catch {
              source = assetUrl;
              if (isDirectRasterImageMime(mimeFromApi)) {
                file = galleryPlaceholderImageFile(
                  resolvedName,
                  resolveGalleryImageMimeForUrl(mimeFromApi),
                );
              }
            }
          }

          const icon =
            !useDirectImageUrl && file ? getMediaTileFileIcon(file) : undefined;

          return {
            fileName: resolvedName,
            description: fileMetadata,
            id: assetId,
            file,
            source,
            name: file?.name ?? resolvedName,
            ...(icon !== undefined ? { icon } : {}),
          };
        }),
      );

      if (cancelled) {
        newBlobUrls.forEach((u) => URL.revokeObjectURL(u));
        return;
      }

      galleryBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      galleryBlobUrlsRef.current = newBlobUrls;
      setResolvedGalleryMedia(items);
      setGalleryMediaLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    agencyName,
    descriptionMode,
    inspectionAssets,
    showGallery,
    shownAssetIds,
  ]);

  return { resolvedGalleryMedia, galleryMediaLoading };
}
